import { useMemo } from 'react'
import { Alert, Text, Table, Skeleton as MSkeleton } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer,
  Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts'
import { categoricalSlots, useThemeColors } from '@lighthouse/design'
import { RestrictedState } from '../access/RestrictedState'
import { historyView } from './combFormat'
import { useColumnHistory } from '../../hooks/useDataComb'
import { errorKind, errorSummary, exactNumber } from '../../utils/format'
import type { CombColumnHistoryPoint } from '../../types/dataComb'

/**
 * How one column's fill rate has moved across recent passes.
 *
 * This is the whole reason the comb persists its measurements instead of just
 * its verdicts. "41% populated" is a number an operator cannot act on. "41%, and
 * it has been 41% every night for a month" is a modelling question; "41%, and it
 * was 98% yesterday" is an incident. The rule cannot tell them apart from a
 * single pass and neither can a person — the series can.
 *
 * One line per provenance, not a blended average, because that is how the
 * profiler measures: a column that is empty for Oracle-loaded rows and populated
 * for hand-entered ones is a completely different diagnosis from one that is
 * uniformly sparse, and averaging them hides exactly that.
 */

/** Recharts needs one row per x value with a column per series. */
interface PivotRow {
  observedAt: string
  runKey: string
  [sourceSystem: string]: string | number
}

function pivot(points: CombColumnHistoryPoint[]): { rows: PivotRow[]; series: string[] } {
  const byRun = new Map<string, PivotRow>()
  const series: string[] = []

  for (const p of points) {
    if (!series.includes(p.sourceSystem)) series.push(p.sourceSystem)

    const existing = byRun.get(p.runId)
    if (existing) {
      existing[p.sourceSystem] = p.fillRatePercent
    } else {
      byRun.set(p.runId, {
        observedAt: p.observedAt,
        runKey: p.runKey,
        [p.sourceSystem]: p.fillRatePercent,
      })
    }
  }

  return { rows: [...byRun.values()], series }
}

export function FillRateHistory({
  schemaName,
  tableName,
  columnName,
}: {
  schemaName: string
  tableName: string
  columnName: string | null
}) {
  const { data, isLoading, isError, error } = useColumnHistory(schemaName, tableName, columnName)
  const t = useThemeColors()
  const slots = categoricalSlots(t)

  const { rows, series } = useMemo(() => pivot(data?.points ?? []), [data])
  const view = historyView(columnName, data, isLoading, isError)

  if (view === 'not-applicable') {
    return (
      <Text size="sm" c="dimmed">
        This finding is about a table rather than a column, so there is no fill rate to trend.
      </Text>
    )
  }

  if (view === 'error') {
    if (errorKind(error) === 'forbidden') {
      return <RestrictedState surface="This column's measurement history" reason={errorSummary(error)} compact />
    }
    // Not the "none retained" sentence below: a failed request says nothing about
    // whether any measurement exists, and conflating the two reads as "this column
    // was never measured".
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="gray" variant="light" title="Could not load the measurement history">
        <Text size="sm">
          The request failed, which says nothing about whether this column has been measured.
          {errorSummary(error) && <> The server said: {errorSummary(error)}</>}
        </Text>
      </Alert>
    )
  }

  // `!data` is loading too, not "no history": a query paused offline or between
  // retries has neither data nor an error yet.
  if (view === 'loading' || !data) return <MSkeleton height={180} />

  if (view === 'empty') {
    return (
      <Text size="sm" c="dimmed">
        No measurements are retained for this column. Profiles are trimmed after 90 days, and a column
        dropped from the model stops being measured.
      </Text>
    )
  }

  // A single point is not a trend, and drawing a one-dot line implies one exists.
  // Say what there is instead, and say why there is only one.
  const latest = data.points.filter((p) => p.runKey === rows[rows.length - 1].runKey)

  return (
    <div>
      {view === 'single' ? (
        <Text size="sm" c="dimmed" mb="sm">
          Only one pass has measured this column. A trend needs at least two — the next nightly run will
          give this a shape.
        </Text>
      ) : (
        <ResponsiveContainer width="100%" height={190}>
          <LineChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={t.chartGrid} vertical={false} />
            <XAxis
              dataKey="observedAt"
              tick={{ fontSize: 10, fill: t.colorTextSecondary }}
              stroke={t.chartAxis}
              tickFormatter={(v: string) =>
                new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
              }
              interval="preserveStartEnd"
              minTickGap={32}
            />
            <YAxis
              width={40}
              domain={[0, 100]}
              tick={{ fontSize: 10, fill: t.colorTextSecondary }}
              stroke={t.chartAxis}
              tickFormatter={(v: number) => `${v}%`}
            />
            <RTooltip
              contentStyle={{
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                borderRadius: 4,
                fontSize: 11,
              }}
              labelFormatter={(v) => new Date(v as string).toLocaleString()}
              formatter={(v, name) => [`${(v as number).toFixed(2)}%`, name as string]}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {series.map((s, i) => (
              <Line
                key={s}
                type="monotone"
                dataKey={s}
                name={s}
                stroke={slots[i % slots.length]}
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}

      {/*
        The exact counts behind the latest point. A percentage answers "how bad",
        the counts answer "how many rows" — and for a decision about whether to
        chase a column, the second one is usually what settles it.
      */}
      <Table mt="sm" fz="xs" withRowBorders={false}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Provenance</Table.Th>
            <Table.Th ta="right">Rows</Table.Th>
            <Table.Th ta="right">Non-null</Table.Th>
            <Table.Th ta="right">Blank</Table.Th>
            <Table.Th ta="right">Zero</Table.Th>
            <Table.Th ta="right">Fill rate</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {latest.map((p) => (
            <Table.Tr key={`${p.runId}-${p.sourceSystem}`}>
              <Table.Td>{p.sourceSystem}</Table.Td>
              <Table.Td ta="right" ff="monospace">{exactNumber(p.rowCount)}</Table.Td>
              <Table.Td ta="right" ff="monospace">{exactNumber(p.nonNullCount)}</Table.Td>
              <Table.Td ta="right" ff="monospace">{exactNumber(p.blankCount)}</Table.Td>
              <Table.Td ta="right" ff="monospace">{exactNumber(p.zeroCount)}</Table.Td>
              <Table.Td ta="right" ff="monospace">{p.fillRatePercent.toFixed(2)}%</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </div>
  )
}
